import { canOfferSectionConversion } from '@/components/tiptap/shared/convert-selection-to-section';
import { type Editor } from '@tiptap/react';

// Inline heading levels the block-style picker offers — H1 is reserved as the
// section-title delimiter (see `INLINE_HEADING_LEVELS` in the extension kit).
const INLINE_HEADING_LEVELS = [2, 3, 4, 5] as const;

// Blocks the picker must never restyle via `setBlockType`: a level-1 heading is
// always a section title; list items carry their own structure; a code block
// would lose its fencing if flattened; and a heading inside a GFM table cell
// serializes as literal `## text` inline, so it silently degrades to plain text
// on the next round-trip. Each would corrupt content or break the round-trip.
const PROTECTED_BLOCK_NODES = new Set([
  'bulletList',
  'orderedList',
  'listItem',
  'codeBlock',
  'tableCell',
  'tableHeader',
]);

/** The active inline heading level (2–5), or null when not in an inline heading. */
export const activeInlineHeadingLevel = (editor: Editor) =>
  INLINE_HEADING_LEVELS.find((level) => editor.isActive('heading', { level })) ?? null;

/**
 * True when the selection touches a block the picker must not restyle. A range
 * scan is required, not `editor.isActive`: `isActive` reports true only when a
 * node covers the WHOLE selection (`range >= selectionRange` in @tiptap/core),
 * so a selection spanning a section title into its body — or Select-All across
 * sections — would slip past an `isActive` gate and let `setBlockType` rewrite
 * the protected H1. Intersecting the range catches every such case.
 */
const selectionTouchesProtectedBlock = (editor: Editor): boolean => {
  const { from, to } = editor.state.selection;
  let touches = false;
  editor.state.doc.nodesBetween(from, to, (node) => {
    if (touches) return false;
    if (
      (node.type.name === 'heading' && node.attrs.level === 1) ||
      PROTECTED_BLOCK_NODES.has(node.type.name)
    ) {
      touches = true;
      return false;
    }
    return true;
  });
  return touches;
};

/** Active-state row for the markdown-native marks/nodes the bubble menu exposes. */
export const selectMarkdownNativeFormatStates = ({ editor }: { editor: Editor }) => ({
  isHeading: editor.isActive('heading'),
  headingLevel: activeInlineHeadingLevel(editor),
  // The block-style picker only appears when the whole selection is safe to
  // convert (no section title, no list) — see `selectionTouchesProtectedBlock`.
  canShowHeadingPicker: !selectionTouchesProtectedBlock(editor),
  isBold: editor.isActive('bold'),
  isItalic: editor.isActive('italic'),
  isUnderline: editor.isActive('underline'),
  isStrike: editor.isActive('strike'),
  isCode: editor.isActive('code'),
  isBulletList: editor.isActive('bulletList'),
  isOrderedList: editor.isActive('orderedList'),
  isLink: editor.isActive('link'),
  canConvertToSection: canOfferSectionConversion(editor.state),
});
